import { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from '@/hooks/useDebounce'
import { FiSearch, FiChevronLeft, FiChevronRight, FiChevronDown, FiPlus, FiMoreVertical, FiRefreshCw } from 'react-icons/fi'
import StatusBadge from '@/components/ui/StatusBadge'
import NewShipmentModal from './NewShipmentModal'
import ShipmentDetailsModal from './ShipmentDetailsModal'
import EditShipmentModal from './EditShipmentModal'
import ShipmentStatusModal from './ShipmentStatusModal'
import {
  getShipments,
  loadShipmentCustomerOptions,
  getShipment,
  deleteShipment,
  deleteAllShipments,
  syncShipmentTracking,
  syncAllShipmentTracking,
  shipmentToDisplay,
  getStatusBadgeFromBackend,
  SHIPMENT_STATUS_OPTIONS,
  INTEGRATED_SHIPPING_LINE_OPTIONS,
  type ShipmentDisplay,
} from '@/services/api/shipments'
import PageSizeSelect from '@/components/common/PageSizeSelect'

export type ShipmentData = ShipmentDisplay

// Status filter uses same values as backend (client-confirmed list)

const Shipments = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentPage, setCurrentPage] = useState(1)
  const [resultsPerPage, setResultsPerPage] = useState(10)
  const [statusFilter, setStatusFilter] = useState('Status')
  const [shippingLineFilter, setShippingLineFilter] = useState('Shipping Line')
  const [customerFilter, setCustomerFilter] = useState('Customer')
  const [customerOptions, setCustomerOptions] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '')
  const debouncedSearch = useDebounce(searchQuery, 400)
  const [shipments, setShipments] = useState<ShipmentData[]>([])
  const [meta, setMeta] = useState<{ page: number; limit: number; total: number; totalPages: number }>({ page: 1, limit: 10, total: 0, totalPages: 0 })
  const [loading, setLoading] = useState(true)
  const [syncingAll, setSyncingAll] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isNewShipmentModalOpen, setIsNewShipmentModalOpen] = useState(false)
  const [isEditShipmentModalOpen, setIsEditShipmentModalOpen] = useState(false)
  const [isShipmentDetailsModalOpen, setIsShipmentDetailsModalOpen] = useState(false)
  const [isShipmentStatusModalOpen, setIsShipmentStatusModalOpen] = useState(false)
  const [selectedShipment, setSelectedShipment] = useState<ShipmentData | null>(null)
  const [openActionIndex, setOpenActionIndex] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [syncingId, setSyncingId] = useState<string | null>(null)
  const [clearingAll, setClearingAll] = useState(false)
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null)

  const fetchShipments = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const statusParam = statusFilter !== 'Status' ? statusFilter : undefined
      const shippingLineParam = shippingLineFilter !== 'Shipping Line' ? shippingLineFilter : undefined
      const customerParam = customerFilter !== 'Customer' ? customerFilter : undefined
      const { shipments: data, meta: paginationMeta } = await getShipments({
        page: currentPage,
        limit: resultsPerPage,
        search: debouncedSearch || undefined,
        status: statusParam,
        shippingLine: shippingLineParam,
        customer: customerParam,
      })
      setShipments(data.map(shipmentToDisplay))
      setMeta(paginationMeta)

      const namesFromPage = [
        ...new Set(
          data
            .map((s) => (s.customer != null ? String(s.customer).trim() : ''))
            .filter((c) => c.length > 0)
        ),
      ]
      if (namesFromPage.length > 0) {
        setCustomerOptions((prev) => {
          const merged = [...new Set([...prev, ...namesFromPage])]
          merged.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
          return merged
        })
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch shipments')
      setShipments([])
    } finally {
      setLoading(false)
    }
  }, [currentPage, debouncedSearch, statusFilter, shippingLineFilter, customerFilter, resultsPerPage])

  useEffect(() => {
    fetchShipments()
  }, [fetchShipments])

  useEffect(() => {
    loadShipmentCustomerOptions()
      .then(setCustomerOptions)
      .catch(() => setCustomerOptions([]))
  }, [])

  // Reset to page 1 when search or filters change so results are not skipped.
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, statusFilter, shippingLineFilter, customerFilter])

  // Auto-refresh list every 5 min so status/ETA from carrier sync (e.g. MSC) stay up to date
  useEffect(() => {
    const intervalMs = 5 * 60 * 1000
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') fetchShipments()
    }, intervalMs)
    return () => clearInterval(timer)
  }, [fetchShipments])

  // Sync URL ?search= into local state when navigating from header search
  useEffect(() => {
    const q = searchParams.get('search')
    if (q !== null) setSearchQuery(q)
  }, [searchParams])

  // When opening from AI Actions (?highlight=id), show that shipment's details and highlight the row
  const highlightId = searchParams.get('highlight')
  useEffect(() => {
    if (!highlightId || loading) return
    const onList = shipments.find((s) => s.id === highlightId)
    if (onList) {
      setSelectedShipment(onList)
      setIsShipmentDetailsModalOpen(true)
      setHighlightedRowId(highlightId)
      setTimeout(() => setHighlightedRowId(null), 4000)
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('highlight')
        return next
      }, { replace: true })
      requestAnimationFrame(() => {
        const row = document.getElementById(`shipment-row-${highlightId}`)
        row?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
      return
    }
    getShipment(highlightId)
      .then((shipment) => {
        setSelectedShipment(shipment)
        setIsShipmentDetailsModalOpen(true)
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          next.delete('highlight')
          return next
        }, { replace: true })
      })
      .catch(() => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          next.delete('highlight')
          return next
        }, { replace: true })
      })
  }, [highlightId, loading, shipments, setSearchParams])

  useEffect(() => {
    const handleDocumentClick = () => setOpenActionIndex(null)
    document.addEventListener('click', handleDocumentClick)
    return () => document.removeEventListener('click', handleDocumentClick)
  }, [])

  // When New Shipment modal is open, hide the main page scrollbar
  useEffect(() => {
    if (!isNewShipmentModalOpen) return

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isNewShipmentModalOpen])

  const totalResults = meta.total
  const totalPages = meta.totalPages || 1
  const currentData = shipments

  const pageItems = (() => {
    const items: (number | 'ellipsis')[] = []
    const maxButtons = 7

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) items.push(i)
      return items
    }

    const first = 1
    const last = totalPages
    const windowSize = 2
    const start = Math.max(currentPage - windowSize, 2)
    const end = Math.min(currentPage + windowSize, totalPages - 1)

    items.push(first)
    if (start > 2) items.push('ellipsis')
    for (let p = start; p <= end; p++) items.push(p)
    if (end < totalPages - 1) items.push('ellipsis')
    items.push(last)

    return items
  })()

  return (
    <div
      className="space-y-4 sm:space-y-6 lg:max-w-[calc(100%-200px)] lg:ml-auto lg:mr-0"
      style={{
        maxWidth: '100%',
        maxHeight: isNewShipmentModalOpen ? '100vh' : undefined,
        overflow: isNewShipmentModalOpen ? 'hidden' : undefined,
      }}
    >
      {/* Header Section */}
      <div>
        {/* First Row: Title and Breadcrumbs on same level */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
          <h1
            className="text-lg sm:text-xl"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '32px',
              letterSpacing: '0%',
              color: '#212126'
            }}
          >
            Shipments
          </h1>

          {/* Breadcrumbs */}
          <span
            className="text-xs sm:text-sm"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '12px',
              lineHeight: '20px',
              letterSpacing: '0%',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Shipments</span>
          </span>
        </div>

        {/* Second Row: Refresh, Clear all and New Shipment Buttons */}
        <div className="flex justify-end gap-3 mt-6 sm:mt-4 mb-4 sm:mb-6">
          <button
            type="button"
            disabled={loading || syncingAll}
            onClick={async () => {
              setSyncingAll(true)
              setError(null)
              try {
                const carrier =
                  shippingLineFilter !== 'Shipping Line' ? shippingLineFilter : undefined
                await syncAllShipmentTracking({ carrier, limit: 100 })
              } catch (err) {
                console.warn('Bulk carrier sync failed:', err)
              } finally {
                setSyncingAll(false)
              }
              await fetchShipments()
            }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            title="Sync carrier tracking (status, ETA) then refresh the list"
          >
            <FiRefreshCw className={loading || syncingAll ? 'animate-spin' : ''} size={16} />
            {syncingAll ? 'Syncing…' : 'Refresh'}
          </button>
          <button
            type="button"
            disabled={clearingAll || totalResults === 0}
            onClick={async () => {
              if (!window.confirm(`Remove all ${totalResults} shipment(s)? Clearing records will also be removed. This cannot be undone.`)) return
              setClearingAll(true)
              setError(null)
              try {
                await deleteAllShipments()
                setCurrentPage(1)
                await fetchShipments()
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to clear shipments')
              } finally {
                setClearingAll(false)
              }
            }}
            className="flex items-center justify-center gap-2 font-medium rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            style={{
              height: '38px',
              backgroundColor: '#FFFFFF',
              border: '1px solid #B91C1C',
              padding: '8px 16px',
              borderRadius: '6px',
              gap: '8px',
              color: '#B91C1C'
            }}
          >
            {clearingAll ? 'Clearing…' : 'Clear all shipments'}
          </button>
          <button
            onClick={() => setIsNewShipmentModalOpen(true)}
            className="flex items-center justify-center gap-2 text-white font-medium rounded-md hover:opacity-90 transition-opacity text-sm sm:text-base"
            style={{
              height: '38px',
              backgroundColor: '#212126',
              padding: '8px 16px',
              borderRadius: '6px',
              gap: '8px'
            }}
          >
            <FiPlus className="w-4 h-4" />
            <span className="hidden sm:inline">New Shipment</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Search and Filters - Above Table */}
        <div className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            {/* Search Bar */}
            <div className="relative flex-1 w-full sm:max-w-md">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search contract, booking, container, BL..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  height: '40px'
                }}
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
              {/* Status Filter */}
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '22px',
                    color: '#69697A',
                    height: '40px',
                    minWidth: '120px'
                  }}
                >
                  <option>Status</option>
                  {SHIPMENT_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <FiChevronDown 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{
                    width: '16px',
                    height: '16px',
                    opacity: 0.8,
                    color: '#050521'
                  }}
                />
              </div>

              {/* Shipping Line Filter */}
              <div className="relative">
                <select
                  value={shippingLineFilter}
                  onChange={(e) => setShippingLineFilter(e.target.value)}
                  className="px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '22px',
                    color: '#69697A',
                    height: '40px',
                    minWidth: '140px'
                  }}
                >
                  <option value="Shipping Line">Shipping Line</option>
                  {INTEGRATED_SHIPPING_LINE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <FiChevronDown 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{
                    width: '16px',
                    height: '16px',
                    opacity: 0.8,
                    color: '#050521'
                  }}
                />
              </div>

              {/* Customer Filter */}
              <div className="relative">
                <select
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                  className="px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '22px',
                    color: '#69697A',
                    height: '40px',
                    minWidth: '120px'
                  }}
                >
                  <option value="Customer">Customer</option>
                  {customerOptions.map((customer) => (
                    <option key={customer} value={customer}>
                      {customer}
                    </option>
                  ))}
                </select>
                <FiChevronDown 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{
                    width: '16px',
                    height: '16px',
                    opacity: 0.8,
                    color: '#050521'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto px-2 sm:px-4" style={{ minWidth: '0' }}>
          <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '1400px' }}>
            <thead>
              <tr>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderLeft: '1px solid #E9E9EA', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap', borderTopLeftRadius: '8px' }}>Buy Contract</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Sale Contract</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Booking</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Bill of Lading</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Container Numbers</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Vessel</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Shipping Line</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Product</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Customer</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Factory</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Origin</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Destination</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>ETD</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>ETA</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Status</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderRight: '1px solid #E9E9EA', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap', borderTopRightRadius: '8px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={16} className="py-12 text-center" style={{ fontFamily: 'Lexend Deca', color: '#69697A' }}>
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={16} className="py-12 text-center" style={{ fontFamily: 'Lexend Deca', color: '#DC2626' }}>
                    {error}
                  </td>
                </tr>
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={16} className="py-12 text-center" style={{ fontFamily: 'Lexend Deca', color: '#69697A' }}>
                    No shipments found
                  </td>
                </tr>
              ) : (
              currentData.map((item) => {
                const statusBadgeProps = getStatusBadgeFromBackend(item.status)

                return (
                  <tr
                    id={`shipment-row-${item.id}`}
                    key={item.id}
                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${highlightedRowId === item.id ? 'bg-blue-50 ring-1 ring-blue-200 ring-inset' : ''}`}
                    onClick={() => {
                      setSelectedShipment(item)
                      setIsShipmentDetailsModalOpen(true)
                    }}
                  >
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderLeft: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.buyContract ?? '—'}
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.saleContract ?? '—'}
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.booking}
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.billOfLading}
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA' }}>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          setSelectedShipment(item)
                          setIsShipmentDetailsModalOpen(true)
                        }}
                        className="underline"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '13px',
                          lineHeight: '22px',
                          color: '#212126',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                        }}
                      >
                        {(item.containerNumbers?.length ?? 0) > 1
                          ? `${item.containerNumbers.length} containers`
                          : item.containerNumber}
                      </button>
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.vessel}
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.shippingLine}
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.product}
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.customer ?? '-'}
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.factory ?? '—'}
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.origin}
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.destination}
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.etd}
                    </td>
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                      {item.eta}
                    </td>
                    <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA' }}>
                      <StatusBadge status={statusBadgeProps.status} label={statusBadgeProps.label} />
                    </td>
                    <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderRight: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA' }}>
                      <div className="relative flex items-center justify-center" onClick={(event) => event.stopPropagation()}>
                        <button
                          className="flex items-center justify-center hover:opacity-80 rounded transition-colors"
                          title="Actions"
                          onClick={(event) => {
                            event.stopPropagation()
                            setOpenActionIndex((prev) => (prev === item.id ? null : item.id))
                          }}
                          style={{
                            backgroundColor: '#EAF1FF',
                            width: '24px',
                            height: '24px',
                            padding: '0'
                          }}
                        >
                          <FiMoreVertical className="w-4 h-4 text-[#2C71F6]" />
                        </button>
                        {openActionIndex === item.id && (
                          <div
                            className="absolute right-0 top-full mt-2 w-40 rounded-lg border border-gray-200 bg-white shadow-lg py-1"
                            style={{ zIndex: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                          >
                            <button
                              className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
                              style={{ fontFamily: 'Lexend Deca', color: '#212126' }}
                              onClick={(event) => {
                                event.stopPropagation()
                                setSelectedShipment(item)
                                setIsEditShipmentModalOpen(true)
                                setOpenActionIndex(null)
                              }}
                            >
                              <span
                                className="inline-flex items-center justify-center rounded shrink-0"
                                style={{ backgroundColor: '#FEF9EB', width: '24px', height: '24px' }}
                              >
                                <img src="/images/edit.svg" alt="" style={{ width: '14px', height: '14px' }} />
                              </span>
                              Edit
                            </button>
                            <button
                              className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
                              style={{ fontFamily: 'Lexend Deca', color: '#212126' }}
                              onClick={(event) => {
                                event.stopPropagation()
                                setSelectedShipment(item)
                                setIsShipmentDetailsModalOpen(true)
                                setOpenActionIndex(null)
                              }}
                            >
                              <span
                                className="inline-flex items-center justify-center rounded shrink-0"
                                style={{ backgroundColor: '#EAF1FF', width: '24px', height: '24px' }}
                              >
                                <img src="/images/eye.svg" alt="" style={{ width: '14px', height: '14px' }} />
                              </span>
                              View
                            </button>
                            <button
                              className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
                              style={{ fontFamily: 'Lexend Deca', color: '#212126' }}
                              onClick={(event) => {
                                event.stopPropagation()
                                setOpenActionIndex(null)
                                setSelectedShipment(item)
                                setIsShipmentStatusModalOpen(true)
                              }}
                            >
                              <span
                                className="inline-flex items-center justify-center rounded shrink-0"
                                style={{ backgroundColor: '#E8F5E9', width: '24px', height: '24px' }}
                              >
                                <img src="/images/map.svg" alt="" style={{ width: '14px', height: '14px' }} />
                              </span>
                              Traceability
                            </button>
                            <button
                              className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
                              style={{ fontFamily: 'Lexend Deca', color: '#212126' }}
                              onClick={async (event) => {
                                event.stopPropagation()
                                setOpenActionIndex(null)
                                setSyncingId(item.id)
                                setError(null)
                                try {
                                  await syncShipmentTracking(item.id)
                                  await fetchShipments()
                                } catch (err) {
                                  setError(err instanceof Error ? err.message : 'Sync failed')
                                } finally {
                                  setSyncingId(null)
                                }
                              }}
                              disabled={syncingId === item.id}
                            >
                              <span
                                className="inline-flex items-center justify-center rounded shrink-0"
                                style={{ backgroundColor: '#EAF1FF', width: '24px', height: '24px' }}
                              >
                                <FiRefreshCw className={syncingId === item.id ? 'animate-spin' : ''} style={{ width: '14px', height: '14px', color: '#2C71F6' }} />
                              </span>
                              {syncingId === item.id ? 'Syncing…' : 'Sync tracking'}
                            </button>
                            <button
                              className="w-full text-left px-3 py-2.5 text-sm hover:bg-red-50 flex items-center gap-2 transition-colors"
                              style={{ fontFamily: 'Lexend Deca', color: '#B91C1C' }}
                              onClick={async (event) => {
                                event.stopPropagation()
                                setOpenActionIndex(null)
                                const label = [item.booking, item.containerNumber].filter(Boolean).join(' ') || 'this shipment'
                                if (!window.confirm(`Delete shipment ${label}? This cannot be undone.`)) return
                                setDeletingId(item.id)
                                setError(null)
                                try {
                                  await deleteShipment(item.id)
                                  if (selectedShipment?.id === item.id) setSelectedShipment(null)
                                  await fetchShipments()
                                } catch (err) {
                                  setError(err instanceof Error ? err.message : 'Failed to delete shipment')
                                } finally {
                                  setDeletingId(null)
                                }
                              }}
                              disabled={deletingId === item.id}
                            >
                              <span
                                className="inline-flex items-center justify-center rounded shrink-0"
                                style={{ backgroundColor: '#FEE2E2', width: '24px', height: '24px' }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                              </span>
                              {deletingId === item.id ? 'Deleting…' : 'Delete'}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
              )}
              {/* Pagination Row */}
              <tr>
                <td colSpan={15} className="py-3 sm:py-4 px-2 sm:px-4" style={{ paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderLeft: '1px solid #E9E9EA', borderRight: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                    <span
                      className="text-xs sm:text-sm"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '22px',
                        color: '#69697A'
                      }}
                    >
                      Showing {currentData.length} of {totalResults} Results
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      <PageSizeSelect
                        value={resultsPerPage}
                        onChange={(size) => {
                          setResultsPerPage(size)
                          setCurrentPage(1)
                        }}
                      />
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '22px',
                          color: '#212126',
                          border: '1px solid #E9E9EA',
                          backgroundColor: 'transparent'
                        }}
                      >
                        <FiChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Previous</span>
                      </button>
                      {pageItems.map((item, idx) =>
                        item === 'ellipsis' ? (
                          <span
                            key={`ellipsis-${idx}`}
                            className="px-2 select-none"
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '22px',
                              color: '#69697A',
                            }}
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={item}
                            onClick={() => setCurrentPage(item)}
                            className="px-3 py-1 rounded hover:opacity-90 transition-opacity"
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '22px',
                              color: currentPage === item ? '#FFFFFF' : '#212126',
                              backgroundColor: currentPage === item ? '#212126' : 'transparent',
                              border: '1px solid #E9E9EA'
                            }}
                          >
                            {item}
                          </button>
                        )
                      )}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '22px',
                          color: '#212126',
                          border: '1px solid #E9E9EA',
                          backgroundColor: 'transparent'
                        }}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <FiChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* New Shipment Modal */}
      <NewShipmentModal 
        isOpen={isNewShipmentModalOpen} 
        onClose={() => setIsNewShipmentModalOpen(false)}
        onSuccess={() => {
          setIsNewShipmentModalOpen(false)
          fetchShipments()
        }}
      />

      {/* Shipment Details Modal */}
      <ShipmentDetailsModal 
        isOpen={isShipmentDetailsModalOpen} 
        onClose={() => {
          setIsShipmentDetailsModalOpen(false)
          setSelectedShipment(null)
          setHighlightedRowId(null)
        }}
        shipmentData={selectedShipment || undefined}
      />

      {/* Edit Shipment Modal */}
      <EditShipmentModal 
        isOpen={isEditShipmentModalOpen} 
        onClose={() => {
          setIsEditShipmentModalOpen(false)
          setSelectedShipment(null)
        }}
        shipmentData={selectedShipment || undefined}
        onSuccess={() => {
          setIsEditShipmentModalOpen(false)
          setSelectedShipment(null)
          fetchShipments()
        }}
      />

      {/* Shipment Status Modal */}
      <ShipmentStatusModal
        isOpen={isShipmentStatusModalOpen}
        onClose={() => {
          setIsShipmentStatusModalOpen(false)
          setSelectedShipment(null)
        }}
        shipmentData={selectedShipment || undefined}
      />
    </div>
  )
}

export default Shipments

